import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { ItemThumbnailWithBadge } from 'components/item-thumbnail-with-badge/item-thumbnail-with-badge';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useOrderDetailsCard } from 'hooks/use-order-details-card';
import { useCreateOrderAriaLabel } from 'utils/accessibility';

import { MappedOrder } from '../types';
import { useReorderButton } from '../use-reorder-button';

import { ActionWrapper, CardContainer, Details, ItemName, StyledLink } from './styled';

export const MenuReorderCard = ({
  order,
  toggleDetailsDrawer,
  drawerIsOpen,
  logViewEvent,
  logReorderEvent,
  logOpenDetailEvent,
}: {
  order: MappedOrder;
  toggleDetailsDrawer: () => void;
  drawerIsOpen: boolean;
  logViewEvent: () => void;
  logReorderEvent: () => void;
  logOpenDetailEvent: () => void;
}) => {
  const createAriaLabel = useCreateOrderAriaLabel();
  const { reorder, disabled } = useReorderButton(order);
  const isMobile = useIsMobileBreakpoint();
  const { formatMessage } = useIntl();
  const linkText = formatMessage({ id: drawerIsOpen ? 'hideDetails' : 'viewDetails' });

  const { itemHighlighted, totalItems, itemNames } = useOrderDetailsCard(order, true);

  useEffectOnce(() => {
    logViewEvent();
  });

  return (
    <CardContainer>
      {itemHighlighted && <ItemThumbnailWithBadge item={itemHighlighted} totalItems={totalItems} />}
      <Details>
        <ItemName numberOfLines={isMobile ? 2 : 1}>{itemNames}</ItemName>
        <StyledLink
          onPress={() => {
            toggleDetailsDrawer();
            logOpenDetailEvent();
          }}
        >
          {linkText}
        </StyledLink>
      </Details>
      <ActionWrapper>
        <ActionButton
          testID="reorder-button"
          onPress={() => {
            if (drawerIsOpen) {
              toggleDetailsDrawer();
            }
            reorder();
            logReorderEvent();
          }}
          disabled={disabled}
          size={ActionButtonSizes.SMALL}
          accessibilityLabel={createAriaLabel({
            buttonType: 'reorder',
            order,
          })}
        >
          {formatMessage({ id: 'reorder' })}
        </ActionButton>
      </ActionWrapper>
    </CardContainer>
  );
};
